import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  FaArrowRight, FaPlus, FaTrash, FaPencilAlt, FaCheck, FaTimes,
  FaSearchPlus, FaSearchMinus, FaCompress, FaChevronDown, FaLayerGroup, FaTag,
} from 'react-icons/fa';
import * as pdfjsLib from 'pdfjs-dist';
import { getApiEndpoint } from '../../../../config/api';
import { getAuthHeader } from '../../../../utils/auth';
import { createCategory, updateCategory, deleteCategory, savePins } from '../../../../services/onboardingWizardService';
import { fetchModuleCatalogue } from '../../../../services/assetIdTemplateService';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

const PALETTE = ['#1C8A7F', '#2563EB', '#9333EA', '#DB2777', '#DC2626', '#EA580C', '#CA8A04', '#65A30D', '#0D9488', '#0284C7'];

// ── Canonical variant + fixing tables (mirrors hydrajaws manual tool) ─────
const VARIANTS = {
  stainless_steel: { label: 'Stainless Steel', code: 'SS' },
  galvanised:      { label: 'Galvanised',      code: 'G'  },
  aluminium:       { label: 'Aluminium',       code: 'AL' },
  steel:           { label: 'Steel',           code: 'ST' },
  concrete:        { label: 'Concrete',        code: 'C'  },
  temporary:       { label: 'Temporary',       code: 'T'  },
  permanent:       { label: 'Permanent',       code: 'P'  },
};

const FIXINGS = {
  chemfix:               { label: 'Chemfix',               code: 'CF' },
  through_bolt:          { label: 'Through Bolt',          code: 'TB' },
  cast_in:               { label: 'Cast In',               code: 'CI' },
  welded:                { label: 'Welded',                code: 'WL' },
  clamped:               { label: 'Clamped',               code: 'CL' },
  bolted_to_steel:       { label: 'Bolted to Steel',       code: 'BS' },
  surface_fixed:         { label: 'Surface Fixed',         code: 'SF' },
  ballasted:             { label: 'Ballasted',             code: 'BA' },
  manufacturer_specific: { label: 'Manufacturer Specific', code: 'MS' },
};

// Subtype factory keeps the table readable
const t = (value, label, code, variants, fixings) => ({ value, label, code, variants, fixings });

// ── Classification taxonomy ───────────────────────────────────────────────
const SECTION_DEFS = {
  roof_safety: {
    assetTypes: {
      anchor_points: { label: 'Anchor Points', code: 'AP', subtypes: [
        t('fixed_anchor',      'Fixed Anchor',      'FX', ['stainless_steel','galvanised','aluminium','steel'], ['chemfix','through_bolt','cast_in','welded','bolted_to_steel','surface_fixed']),
        t('eyebolt',           'Eyebolt',           'EB', ['stainless_steel','galvanised','steel'],              ['chemfix','through_bolt','cast_in','welded','bolted_to_steel','surface_fixed']),
        t('chemical_anchor',   'Chemical Anchor',   'CH', ['concrete','steel'],                                  ['chemfix','through_bolt','surface_fixed']),
        t('mechanical_anchor', 'Mechanical Anchor', 'ME', ['steel','stainless_steel'],                           ['through_bolt','bolted_to_steel','surface_fixed']),
        t('concrete_anchor',   'Concrete Anchor',   'CO', ['concrete'],                                          ['cast_in','chemfix','through_bolt']),
        t('steel_anchor',      'Steel Anchor',      'ST', ['steel','galvanised'],                                ['welded','bolted_to_steel','clamped','surface_fixed']),
        t('timber_anchor',     'Timber Anchor',     'TI', ['temporary','permanent'],                             ['through_bolt','surface_fixed','manufacturer_specific']),
        t('mobile_anchor',     'Mobile Anchor',     'MO', ['temporary'],                                         ['ballasted','clamped','manufacturer_specific']),
        t('temporary_anchor',  'Temporary Anchor',  'TA', ['temporary'],                                         ['ballasted','clamped','surface_fixed','manufacturer_specific']),
      ]},
      davit_sockets: { label: 'Davit Sockets', code: 'DS', subtypes: [
        t('cast_in_socket',         'Cast-in Socket',         'CI', ['stainless_steel','galvanised'],           ['cast_in']),
        t('wall_mounted_socket',    'Wall Mounted Socket',    'WM', ['stainless_steel','galvanised','aluminium'], ['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
        t('surface_mounted_socket', 'Surface Mounted Socket', 'SM', ['stainless_steel','galvanised'],           ['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
        t('removable_socket',       'Removable Socket',       'RS', ['stainless_steel','galvanised'],           ['clamped','bolted_to_steel','surface_fixed','manufacturer_specific']),
        t('temporary_socket',       'Temporary Socket',       'TS', ['temporary'],                               ['ballasted','clamped','surface_fixed']),
        t('recessed_socket',        'Recessed Socket',        'RC', ['stainless_steel','galvanised'],           ['cast_in','chemfix','through_bolt']),
        t('parapet_mounted_socket', 'Parapet Mounted Socket', 'PM', ['stainless_steel','galvanised'],           ['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
      ]},
      lifelines: { label: 'Lifelines', code: 'LL', subtypes: [
        t('horizontal_lifeline', 'Horizontal Lifeline', 'HL', ['galvanised','stainless_steel','steel'], ['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
        t('vertical_lifeline',   'Vertical Lifeline',   'VL', ['galvanised','stainless_steel'],         ['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
        t('rigid_lifeline',      'Rigid Lifeline',      'RL', ['steel','aluminium'],                    ['welded','bolted_to_steel','clamped','surface_fixed']),
        t('flexible_lifeline',   'Flexible Lifeline',   'FL', ['temporary','permanent'],                ['clamped','surface_fixed','manufacturer_specific']),
        t('cable_lifeline',      'Cable Lifeline',      'CL', ['galvanised','stainless_steel'],         ['clamped','surface_fixed','manufacturer_specific']),
        t('rail_system',         'Rail System',         'RS', ['steel','aluminium'],                    ['welded','bolted_to_steel','surface_fixed']),
      ]},
      abseil_systems: { label: 'Abseil Systems', code: 'AS', subtypes: [
        t('abseil_anchor',       'Abseil Anchor',       'AA', ['stainless_steel','steel'],      ['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
        t('rope_access_anchor',  'Rope Access Anchor',  'RA', ['stainless_steel','steel'],      ['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
        t('abseil_rail',         'Abseil Rail',         'AR', ['stainless_steel','aluminium'],  ['welded','bolted_to_steel','surface_fixed']),
        t('rope_access_line',    'Rope Access Line',    'RL', ['temporary','permanent'],        ['clamped','surface_fixed','manufacturer_specific']),
        t('abseil_davit',        'Abseil Davit',        'AD', ['stainless_steel','galvanised'], ['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
      ]},
      cradles_bmus: { label: 'Cradles / BMUs', code: 'CR', subtypes: [
        t('building_maintenance_unit', 'Building Maintenance Unit', 'BM', ['permanent'],            ['welded','bolted_to_steel','surface_fixed','manufacturer_specific']),
        t('fixed_cradle',              'Fixed Cradle',              'FC', ['steel','aluminium'],    ['welded','bolted_to_steel','surface_fixed']),
        t('monorail_cradle',           'Monorail Cradle',           'MR', ['steel','aluminium'],    ['welded','bolted_to_steel','surface_fixed']),
        t('track_system',              'Track System',              'TS', ['steel','aluminium'],    ['welded','bolted_to_steel','surface_fixed']),
        t('davit_cradle',              'Davit Cradle',              'DC', ['steel','aluminium'],    ['welded','bolted_to_steel','surface_fixed']),
        t('suspended_platform',        'Suspended Platform',        'SP', ['temporary','permanent'], ['clamped','surface_fixed','manufacturer_specific']),
      ]},
      edge_protection: { label: 'Edge Protection', code: 'EP', subtypes: [
        t('temporary_edge_protection', 'Temporary Edge Protection', 'TE', ['temporary'],                              ['ballasted','clamped','surface_fixed']),
        t('permanent_edge_protection', 'Permanent Edge Protection', 'PE', ['permanent','stainless_steel','galvanised'], ['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
        t('parapet_guard',             'Parapet Guard',             'PG', ['stainless_steel','galvanised','aluminium'], ['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
        t('handrail_system',           'Handrail System',           'HR', ['stainless_steel','galvanised','aluminium'], ['welded','bolted_to_steel','surface_fixed']),
        t('ballasted_system',          'Ballasted System',          'BS', ['temporary'],                                ['ballasted']),
      ]},
      walkways_access: { label: 'Walkways / Access', code: 'WA', subtypes: [
        t('roof_walkway',     'Roof Walkway',    'RW', ['steel','aluminium','galvanised'], ['welded','bolted_to_steel','surface_fixed']),
        t('step_over',        'Step-over',       'SO', ['steel','aluminium'],              ['welded','bolted_to_steel','surface_fixed']),
        t('access_platform',  'Access Platform', 'AP', ['steel','aluminium'],              ['welded','bolted_to_steel','surface_fixed']),
        t('roof_hatch',       'Roof Hatch',      'RH', ['steel','aluminium'],              ['welded','bolted_to_steel','surface_fixed']),
        t('cat_ladder',       'Cat Ladder',      'CL', ['steel','aluminium','galvanised'], ['welded','bolted_to_steel','surface_fixed']),
      ]},
      ladders: { label: 'Ladders', code: 'LD', subtypes: [
        t('fixed_ladder',     'Fixed Ladder',     'FL', ['steel','aluminium','galvanised'], ['welded','bolted_to_steel','surface_fixed']),
        t('vertical_ladder',  'Vertical Ladder',  'VL', ['steel','aluminium','galvanised'], ['welded','bolted_to_steel','surface_fixed']),
        t('inclined_ladder',  'Inclined Ladder',  'IL', ['steel','aluminium'],              ['welded','bolted_to_steel','surface_fixed']),
        t('caged_ladder',     'Caged Ladder',     'CL', ['steel','galvanised'],             ['welded','bolted_to_steel','surface_fixed']),
        t('roof_ladder',      'Roof Ladder',      'RL', ['steel','aluminium'],              ['welded','bolted_to_steel','surface_fixed']),
      ]},
      guardrails_handrails: { label: 'Guardrails / Handrails', code: 'GR', subtypes: [
        t('guardrail',  'Guardrail',  'GR', ['stainless_steel','galvanised','aluminium','steel'], ['welded','bolted_to_steel','surface_fixed']),
        t('handrail',   'Handrail',   'HR', ['stainless_steel','galvanised','aluminium'],         ['welded','bolted_to_steel','surface_fixed']),
        t('balustrade', 'Balustrade', 'BL', ['stainless_steel','aluminium'],                      ['welded','bolted_to_steel','surface_fixed']),
        t('knee_rail',  'Knee Rail',  'KR', ['steel','galvanised'],                               ['welded','bolted_to_steel','surface_fixed']),
      ]},
      roof_anchors_non_fall_arrest: { label: 'Roof Anchors (Non-fall arrest)', code: 'RA', subtypes: [
        t('non_fall_arrest_anchor', 'Non-fall Arrest Anchor', 'NF', ['stainless_steel','steel'], ['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
        t('tie_off_point',          'Tie-off Point',          'TP', ['stainless_steel','steel'], ['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
      ]},
      rescue_equipment: { label: 'Rescue Equipment', code: 'RE', subtypes: [
        t('rescue_winch',      'Rescue Winch',      'RW', ['permanent'],                 ['welded','bolted_to_steel','surface_fixed','manufacturer_specific']),
        t('rescue_kit',        'Rescue Kit',        'RK', ['temporary','permanent'],      ['clamped','surface_fixed','manufacturer_specific']),
        t('retrieval_device',  'Retrieval Device',  'RD', ['temporary','permanent'],      ['clamped','surface_fixed','manufacturer_specific']),
        t('rescue_anchor',     'Rescue Anchor',     'RA', ['stainless_steel','steel'],    ['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
      ]},
      temporary_works: { label: 'Temporary Works', code: 'TW', subtypes: [
        t('temporary_anchor',    'Temporary Anchor',    'TA', ['temporary'], ['ballasted','clamped','surface_fixed']),
        t('temporary_guardrail', 'Temporary Guardrail', 'TG', ['temporary'], ['ballasted','clamped','surface_fixed']),
        t('temporary_platform',  'Temporary Platform',  'TP', ['temporary'], ['ballasted','clamped','surface_fixed']),
      ]},
    },
  },
};

// Resolve the currently-selected subtype from a form
const getSubtype = (moduleKey, assetTypeKey, subtypeValue) => {
  const typeData = SECTION_DEFS[moduleKey]?.assetTypes[assetTypeKey];
  return typeData?.subtypes?.find(s => s.value === subtypeValue) || typeData?.subtypes?.[0] || null;
};

// List of variants allowed for the current subtype ({value,label,code} entries)
const variantsFor = (sub) => (sub?.variants || []).map(v => ({ value: v, ...VARIANTS[v] }));
// List of fixings allowed for the current subtype
const fixingsFor = (sub) => (sub?.fixings || []).map(f => ({ value: f, ...FIXINGS[f] }));

function makeDefaultForm(moduleKey, assetTypeOptions = []) {
  const sectionDef = SECTION_DEFS[moduleKey];
  if (sectionDef) {
    const firstTypeKey = Object.keys(sectionDef.assetTypes)[0];
    const firstSubtype = sectionDef.assetTypes[firstTypeKey]?.subtypes?.[0];
    return {
      assetTypeKey: firstTypeKey,
      subtypeValue: firstSubtype?.value || '',
      material: firstSubtype?.variants?.[0] || '',
      fixing: firstSubtype?.fixings?.[0] || '',
      sequenceNumber: '',
    };
  }
  return {
    assetTypeKey: assetTypeOptions[0]?.key || '',
    subtypeValue: '',
    material: '',
    fixing: '',
    sequenceNumber: '',
  };
}

// ── ClassifyForm sub-component ────────────────────────────────────────────
function ClassifyForm({ pin, form, moduleKey, assetTypeOptions, onUpdate, onCategorise, saving }) {
  const sectionDef = SECTION_DEFS[moduleKey];
  const typeData = sectionDef?.assetTypes[form.assetTypeKey];
  const subtypes = typeData?.subtypes || [];
  const currentSubtype = getSubtype(moduleKey, form.assetTypeKey, form.subtypeValue);
  const variantOptions = variantsFor(currentSubtype);
  const fixingOptions = fixingsFor(currentSubtype);
  const variantChosen = !!form.material && variantOptions.some(v => v.value === form.material);

  return (
    <div className="ow-classify-form">
      <div className="ow-classify-fields">
        {/* Category */}
        <div className="ow-classify-field ow-classify-field-wide">
          <label>Category *</label>
          <select value={form.assetTypeKey} onChange={e => onUpdate('assetTypeKey', e.target.value)}>
            {sectionDef
              ? Object.entries(sectionDef.assetTypes).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)
              : assetTypeOptions.map(a => <option key={a.key} value={a.key}>{a.label}</option>)
            }
          </select>
        </div>

        {/* Type */}
        {subtypes.length > 0 && (
          <div className="ow-classify-field ow-classify-field-wide">
            <label>Type *</label>
            <select value={form.subtypeValue} onChange={e => onUpdate('subtypeValue', e.target.value)}>
              {subtypes.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        )}

        {/* Variant (filtered by Type) */}
        <div className="ow-classify-field">
          <label>Variant</label>
          <select
            value={form.material || ''}
            onChange={e => onUpdate('material', e.target.value)}
            disabled={!variantOptions.length}
          >
            {variantOptions.length === 0 && <option value="">None</option>}
            {variantOptions.map(v => <option key={v.value} value={v.value}>{v.label} ({v.code})</option>)}
          </select>
        </div>

        {/* Fixing Method (filtered by Type, locked until Variant chosen) */}
        <div className="ow-classify-field">
          <label>Fixing Method *</label>
          <select
            value={form.fixing || ''}
            onChange={e => onUpdate('fixing', e.target.value)}
            disabled={!variantChosen || !fixingOptions.length}
          >
            {!variantChosen && <option value="">Select variant first…</option>}
            {variantChosen && fixingOptions.map(f => <option key={f.value} value={f.value}>{f.label} ({f.code})</option>)}
          </select>
        </div>

        {/* Sequence number */}
        <div className="ow-classify-field ow-classify-field-seq">
          <label>Seq # *</label>
          <input
            type="number"
            min="1"
            max="999"
            placeholder="001"
            value={form.sequenceNumber}
            onChange={e => onUpdate('sequenceNumber', e.target.value)}
          />
        </div>
      </div>

      <div className="ow-classify-footer">
        <button
          className="ow-btn ow-btn-primary ow-btn-sm"
          onClick={onCategorise}
          disabled={saving}
        >
          <FaCheck /> {saving ? 'Saving…' : 'Categorise'}
        </button>
      </div>
    </div>
  );
}

// ── BulkForm sub-component ────────────────────────────────────────────────
function BulkFormFields({ form, moduleKey, assetTypeOptions, onUpdate }) {
  const sectionDef = SECTION_DEFS[moduleKey];
  const typeData = sectionDef?.assetTypes[form.assetTypeKey];
  const subtypes = typeData?.subtypes || [];
  const currentSubtype = getSubtype(moduleKey, form.assetTypeKey, form.subtypeValue);
  const variantOptions = variantsFor(currentSubtype);
  const fixingOptions = fixingsFor(currentSubtype);
  const variantChosen = !!form.material && variantOptions.some(v => v.value === form.material);

  return (
    <div className="ow-classify-fields">
      <div className="ow-classify-field ow-classify-field-wide">
        <label>Category *</label>
        <select value={form.assetTypeKey} onChange={e => onUpdate('assetTypeKey', e.target.value)}>
          {sectionDef
            ? Object.entries(sectionDef.assetTypes).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)
            : assetTypeOptions.map(a => <option key={a.key} value={a.key}>{a.label}</option>)
          }
        </select>
      </div>
      {subtypes.length > 0 && (
        <div className="ow-classify-field ow-classify-field-wide">
          <label>Type *</label>
          <select value={form.subtypeValue} onChange={e => onUpdate('subtypeValue', e.target.value)}>
            {subtypes.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      )}
      <div className="ow-classify-field">
        <label>Variant</label>
        <select
          value={form.material || ''}
          onChange={e => onUpdate('material', e.target.value)}
          disabled={!variantOptions.length}
        >
          {variantOptions.length === 0 && <option value="">None</option>}
          {variantOptions.map(v => <option key={v.value} value={v.value}>{v.label} ({v.code})</option>)}
        </select>
      </div>
      <div className="ow-classify-field">
        <label>Fixing Method *</label>
        <select
          value={form.fixing || ''}
          onChange={e => onUpdate('fixing', e.target.value)}
          disabled={!variantChosen || !fixingOptions.length}
        >
          {!variantChosen && <option value="">Select variant first…</option>}
          {variantChosen && fixingOptions.map(f => <option key={f.value} value={f.value}>{f.label} ({f.code})</option>)}
        </select>
      </div>
    </div>
  );
}

// Rebuild a classify form from the JSON stored in name_reference
function makeFormFromMeta(meta, moduleKey, assetTypeOptions) {
  const sectionDef = SECTION_DEFS[moduleKey];
  let assetTypeKey = '';
  if (sectionDef) {
    for (const [typeKey, typeData] of Object.entries(sectionDef.assetTypes)) {
      if (typeData.subtypes?.some(s => s.value === meta.subtype)) {
        assetTypeKey = typeKey;
        break;
      }
    }
    if (!assetTypeKey) assetTypeKey = Object.keys(sectionDef.assetTypes)[0];
  } else {
    assetTypeKey = assetTypeOptions[0]?.key || '';
  }
  return {
    assetTypeKey,
    subtypeValue: meta.subtype || '',
    material: meta.material || '',
    fixing: meta.fixing || '',
    sequenceNumber: meta.seq || '',
  };
}

// ── Main component ────────────────────────────────────────────────────────
function Step3Categorise({ session, propertyId, pins: initialPins, categories: initialCategories, onApplyBundle, onAdvance, onFlush }) {
  const imgRef = useRef();
  const vpRef = useRef();
  const [imageSrc, setImageSrc] = useState(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [frameSize, setFrameSize] = useState(null);
  const recalcFrameSize = useCallback(() => {
    const img = imgRef.current; const vp = vpRef.current;
    if (!img || !vp || !img.naturalWidth) return;
    const padding = 24;
    const maxW = vp.clientWidth - padding * 2;
    const maxH = vp.clientHeight - padding * 2;
    const aspect = img.naturalWidth / img.naturalHeight;
    let w = maxW, h = w / aspect;
    if (h > maxH) { h = maxH; w = h * aspect; }
    setFrameSize({ w, h });
  }, []);
  useEffect(() => { window.addEventListener('resize', recalcFrameSize); return () => window.removeEventListener('resize', recalcFrameSize); }, [recalcFrameSize]);

  const [pins, setPins] = useState(initialPins || []);
  const [categories, setCategories] = useState(initialCategories || []);
  const [selected, setSelected] = useState(new Set());
  const [modules, setModules] = useState([]);

  const [editingCatId, setEditingCatId] = useState(null);
  const [newCategoryDraft, setNewCategoryDraft] = useState(null);
  const [sidebarFilter, setSidebarFilter] = useState('uncategorised');

  // Per-pin accordion + classify forms
  const [expandedPinId, setExpandedPinId] = useState(null);
  const [pinForms, setPinForms] = useState({});
  const [categorisingPinId, setCategorisingPinId] = useState(null);

  // Bulk actions
  const [bulkPanelOpen, setBulkPanelOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState(() => makeDefaultForm(session.module_key));

  const [drag, setDrag] = useState(null);
  const [viewportActive, setViewportActive] = useState(false);

  // Refs for stale-closure-free wheel handler
  const scaleRef = useRef(scale);
  const offsetRef = useRef(offset);
  const viewportActiveRef = useRef(false);
  useEffect(() => { scaleRef.current = scale; }, [scale]);
  useEffect(() => { offsetRef.current = offset; }, [offset]);
  useEffect(() => { viewportActiveRef.current = viewportActive; }, [viewportActive]);

  // Load plan image
  useEffect(() => {
    if (!session.document_id) return;
    let cancelled = false;
    (async () => {
      try {
        const url = getApiEndpoint(`/api/properties/${propertyId}/documents/${session.document_id}/download`);
        const res = await fetch(url, { headers: { ...getAuthHeader() } });
        const bytes = await (await res.blob()).arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(bytes), isEvalSupported: false }).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width; canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        if (!cancelled) setImageSrc(canvas.toDataURL('image/png'));
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [session.document_id, propertyId]);

  useEffect(() => {
    fetchModuleCatalogue().then(r => setModules(r?.modules || [])).catch(() => {});
  }, []);

  useEffect(() => { setPins(initialPins || []); }, [initialPins?.length]);
  useEffect(() => { setCategories(initialCategories || []); }, [initialCategories?.length]);

  const activeModule = modules.find(m => m.key === session.module_key);
  const assetTypeOptions = activeModule?.asset_types || [];

  const catsById = useMemo(() => Object.fromEntries(categories.map(c => [c.id, c])), [categories]);

  const getPercentAt = (clientX, clientY) => {
    const el = imgRef.current; if (!el) return null;
    const r = el.getBoundingClientRect();
    return { x: Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100)), y: Math.max(0, Math.min(100, ((clientY - r.top) / r.height) * 100)) };
  };

  const onMouseDownPlan = (e) => {
    if (viewportActiveRef.current) {
      // Already in zoom mode — drag pans the drawing
      setDrag({ type: 'pan', lastX: e.clientX, lastY: e.clientY });
      return;
    }
    // First click into viewport activates zoom mode; don't start a drag yet
    setViewportActive(true);
    viewportActiveRef.current = true;
  };
  const onMouseDownPin = (e, pin) => {
    e.stopPropagation();
    if (e.shiftKey) {
      setSelected(prev => { const n = new Set(prev); if (n.has(pin.id)) n.delete(pin.id); else n.add(pin.id); return n; });
    } else {
      setSelected(new Set([pin.id]));
    }
  };
  const onMouseMove = (e) => {
    if (!drag) return;
    if (drag.type === 'pan') {
      const dx = e.clientX - drag.lastX;
      const dy = e.clientY - drag.lastY;
      setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      setDrag({ ...drag, lastX: e.clientX, lastY: e.clientY });
      return;
    }
    const pct = getPercentAt(e.clientX, e.clientY); if (!pct) return;
    if (drag.type === 'pending') {
      const d = Math.hypot(e.clientX - drag.origin.x, e.clientY - drag.origin.y);
      if (d > 5) setDrag({ type: 'marquee', startPct: drag.percent, endPct: pct, additive: drag.additive });
    } else if (drag.type === 'marquee') {
      setDrag({ ...drag, endPct: pct });
    }
  };
  const onMouseUp = () => {
    if (!drag) return;
    if (drag.type === 'pan') { setDrag(null); return; }
    if (drag.type === 'marquee') {
      const [x1, x2] = [Math.min(drag.startPct.x, drag.endPct.x), Math.max(drag.startPct.x, drag.endPct.x)];
      const [y1, y2] = [Math.min(drag.startPct.y, drag.endPct.y), Math.max(drag.startPct.y, drag.endPct.y)];
      const hits = pins.filter(p => p.x_percent >= x1 && p.x_percent <= x2 && p.y_percent >= y1 && p.y_percent <= y2);
      setSelected(prev => { const n = drag.additive ? new Set(prev) : new Set(); hits.forEach(p => n.add(p.id)); return n; });
    } else if (drag.type === 'pending') {
      if (!drag.additive) setSelected(new Set());
    }
    setDrag(null);
  };
  useEffect(() => {
    const up = () => onMouseUp();
    const mv = (e) => onMouseMove(e);
    window.addEventListener('mouseup', up); window.addEventListener('mousemove', mv);
    return () => { window.removeEventListener('mouseup', up); window.removeEventListener('mousemove', mv); };
  });

  // Scroll-wheel zoom — non-passive so we can preventDefault
  useEffect(() => {
    const vp = vpRef.current;
    if (!vp) return;
    const handleWheel = (e) => {
      if (!viewportActiveRef.current) return;
      e.preventDefault();
      const rect = vp.getBoundingClientRect();
      const cursorX = e.clientX - rect.left;
      const cursorY = e.clientY - rect.top;
      const cur = scaleRef.current;
      const off = offsetRef.current;
      const factor = e.deltaY < 0 ? 1.04 : 0.96;
      const newScale = Math.max(0.3, Math.min(4, cur * factor));
      // Keep the content point under the cursor pinned
      const contentX = (cursorX - off.x) / cur;
      const contentY = (cursorY - off.y) / cur;
      setScale(newScale);
      setOffset({ x: cursorX - contentX * newScale, y: cursorY - contentY * newScale });
    };
    vp.addEventListener('wheel', handleWheel, { passive: false });
    return () => vp.removeEventListener('wheel', handleWheel);
  }, []); // empty deps — uses refs so always current

  // Deactivate zoom mode when clicking outside the viewport
  useEffect(() => {
    const onDown = (e) => {
      if (vpRef.current && !vpRef.current.contains(e.target)) {
        setViewportActive(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const marqueeRect = drag?.type === 'marquee' ? {
    left: Math.min(drag.startPct.x, drag.endPct.x), top: Math.min(drag.startPct.y, drag.endPct.y),
    width: Math.abs(drag.endPct.x - drag.startPct.x), height: Math.abs(drag.endPct.y - drag.startPct.y)
  } : null;

  // ——— Category actions ———
  const startAddCategory = () => {
    setNewCategoryDraft({
      name: '', color: nextColor(categories, PALETTE),
      target_asset_type_key: assetTypeOptions[0]?.key || ''
    });
  };
  const saveNewCategory = async () => {
    if (!newCategoryDraft?.name?.trim()) return toast.error('Category name required');
    if (!newCategoryDraft.target_asset_type_key) return toast.error('Pick an asset type');
    try {
      const r = await createCategory(session.id, {
        name: newCategoryDraft.name.trim(),
        color: newCategoryDraft.color,
        target_asset_type_key: newCategoryDraft.target_asset_type_key,
        sort_order: categories.length
      });
      setCategories(prev => [...prev, r.category]);
      setNewCategoryDraft(null);
    } catch (e) { toast.error(e.message || 'Failed to create'); }
  };
  const cancelNewCategory = () => setNewCategoryDraft(null);

  const saveCategoryEdit = async (cat, patch) => {
    try {
      const r = await updateCategory(cat.id, { ...cat, ...patch });
      setCategories(prev => prev.map(c => c.id === cat.id ? r.category : c));
      setEditingCatId(null);
    } catch (e) { toast.error(e.message || 'Failed to update'); }
  };

  const removeCategory = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? Pins in it will become uncategorised.`)) return;
    try {
      await deleteCategory(cat.id);
      setCategories(prev => prev.filter(c => c.id !== cat.id));
      setPins(prev => prev.map(p => p.category_id === cat.id ? { ...p, category_id: null } : p));
    } catch (e) { toast.error(e.message || 'Failed to delete'); }
  };

  // ——— Pin assignment (map toolbar) ———
  const assignSelectedTo = async (categoryId) => {
    if (!selected.size) return toast.error('Select some pins first');
    const next = pins.map(p => selected.has(p.id) ? { ...p, category_id: categoryId } : p);
    setPins(next);
    try {
      const bundle = await savePins(session.id, next);
      onApplyBundle(bundle);
      toast.success(`${selected.size} pin${selected.size > 1 ? 's' : ''} assigned`);
    } catch (e) { toast.error(e.message || 'Failed to save'); }
  };

  // ——— Pin classify (accordion form) ———
  const handlePinItemClick = (pin) => {
    const isOpening = expandedPinId !== pin.id;
    setExpandedPinId(isOpening ? pin.id : null);
    if (isOpening && !pinForms[pin.id]) {
      let form;
      if (pin.category_id && pin.name_reference) {
        try {
          const meta = JSON.parse(pin.name_reference);
          form = makeFormFromMeta(meta, session.module_key, assetTypeOptions);
        } catch {
          form = makeDefaultForm(session.module_key, assetTypeOptions);
        }
      } else {
        form = makeDefaultForm(session.module_key, assetTypeOptions);
      }
      setPinForms(prev => ({ ...prev, [pin.id]: form }));
    }
  };

  const togglePinSelected = (e, pinId) => {
    e.stopPropagation();
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(pinId)) n.delete(pinId); else n.add(pinId);
      return n;
    });
  };

  const updatePinForm = (pinId, field, value) => {
    setPinForms(prev => {
      const cur = prev[pinId] || makeDefaultForm(session.module_key, assetTypeOptions);
      const updated = { ...cur, [field]: value };
      if (field === 'assetTypeKey') {
        const typeData = SECTION_DEFS[session.module_key]?.assetTypes[value];
        const firstSub = typeData?.subtypes?.[0];
        updated.subtypeValue = firstSub?.value || '';
        updated.material = firstSub?.variants?.[0] || '';
        updated.fixing = firstSub?.fixings?.[0] || '';
      }
      if (field === 'subtypeValue') {
        const sub = getSubtype(session.module_key, cur.assetTypeKey, value);
        updated.material = sub?.variants?.[0] || '';
        updated.fixing = sub?.fixings?.[0] || '';
      }
      if (field === 'material') {
        // Keep fixing only if it's valid for the current subtype
        const sub = getSubtype(session.module_key, cur.assetTypeKey, cur.subtypeValue);
        const valid = (sub?.fixings || []).includes(updated.fixing);
        if (!valid) updated.fixing = sub?.fixings?.[0] || '';
      }
      return { ...prev, [pinId]: updated };
    });
  };

  const getOrEnsureCategory = async (assetTypeKey) => {
    const existing = categories.find(c => c.target_asset_type_key === assetTypeKey);
    if (existing) return existing;
    const sectionDef = SECTION_DEFS[session.module_key];
    const typeLabel = sectionDef?.assetTypes[assetTypeKey]?.label
      || assetTypeOptions.find(a => a.key === assetTypeKey)?.label
      || assetTypeKey;
    const r = await createCategory(session.id, {
      name: typeLabel,
      color: nextColor(categories, PALETTE),
      target_asset_type_key: assetTypeKey,
      sort_order: categories.length,
    });
    setCategories(prev => [...prev, r.category]);
    return r.category;
  };

  const handleCategorisePin = async (pin) => {
    const form = pinForms[pin.id];
    if (!form?.assetTypeKey) return toast.error('Select an asset type');
    if (!String(form?.sequenceNumber || '').trim()) return toast.error('Enter a sequence number for Pin ' + pin.temp_number);
    setCategorisingPinId(pin.id);
    try {
      const cat = await getOrEnsureCategory(form.assetTypeKey);
      const meta = JSON.stringify({
        subtype: form.subtypeValue,
        material: form.material,
        fixing: form.fixing,
        seq: String(form.sequenceNumber).padStart(3, '0'),
      });
      const updatedPins = pins.map(p =>
        p.id === pin.id ? { ...p, category_id: cat.id, name_reference: meta } : p
      );
      setPins(updatedPins);
      const bundle = await savePins(session.id, updatedPins);
      onApplyBundle(bundle);
      toast.success(`Pin ${pin.temp_number} categorised`);
      setExpandedPinId(null);
    } catch (e) {
      toast.error(e.message || 'Failed to categorise');
    } finally {
      setCategorisingPinId(null);
    }
  };

  // ——— Bulk actions ———
  const updateBulkForm = (field, value) => {
    setBulkForm(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'assetTypeKey') {
        const typeData = SECTION_DEFS[session.module_key]?.assetTypes[value];
        const firstSub = typeData?.subtypes?.[0];
        updated.subtypeValue = firstSub?.value || '';
        updated.material = firstSub?.variants?.[0] || '';
        updated.fixing = firstSub?.fixings?.[0] || '';
      }
      if (field === 'subtypeValue') {
        const sub = getSubtype(session.module_key, prev.assetTypeKey, value);
        updated.material = sub?.variants?.[0] || '';
        updated.fixing = sub?.fixings?.[0] || '';
      }
      if (field === 'material') {
        const sub = getSubtype(session.module_key, prev.assetTypeKey, prev.subtypeValue);
        const valid = (sub?.fixings || []).includes(updated.fixing);
        if (!valid) updated.fixing = sub?.fixings?.[0] || '';
      }
      return updated;
    });
  };

  const handleBulkApply = () => {
    if (!uncatSelected.length) return;
    const updates = {};
    uncatSelected.forEach(pin => {
      updates[pin.id] = {
        ...(pinForms[pin.id] || makeDefaultForm(session.module_key, assetTypeOptions)),
        assetTypeKey: bulkForm.assetTypeKey,
        subtypeValue: bulkForm.subtypeValue,
        material: bulkForm.material,
        fixing: bulkForm.fixing,
      };
    });
    setPinForms(prev => ({ ...prev, ...updates }));
    setBulkPanelOpen(false);
    toast.success(`Applied to ${uncatSelected.length} pin${uncatSelected.length !== 1 ? 's' : ''} — enter sequence numbers to categorise`);
  };

  // ——— Counts ———
  const pinCountByCat = useMemo(() => {
    const c = {};
    for (const p of pins) { if (p.category_id) c[p.category_id] = (c[p.category_id] || 0) + 1; }
    return c;
  }, [pins]);
  const uncategorisedCount = pins.filter(p => !p.category_id).length;
  const uncatPins = useMemo(() => pins.filter(p => !p.category_id), [pins]);
  const uncatSelected = useMemo(() => uncatPins.filter(p => selected.has(p.id)), [uncatPins, selected]);

  const continueNext = async () => {
    if (uncategorisedCount > 0) return toast.error(`${uncategorisedCount} pin${uncategorisedCount > 1 ? 's are' : ' is'} not categorised`);
    await onFlush();
    onAdvance(4);
  };

  // ——— Render ———
  return (
    <div className="ow-step-body ow-step-wide">
      <section className="ow-card ow-card-tight">
        <header className="ow-card-head ow-plan-head">
          <div>
            <div className="ow-card-kicker">Step 3 · Categorise</div>
            <h2 className="ow-card-title">Assign pins to categories</h2>
            <p className="ow-card-sub ow-plan-hint">
              Select a pin to classify it, or use <strong>Bulk actions</strong> to apply a classification to multiple pins at once.
            </p>
          </div>
          <div className="ow-plan-counters">
            <div className="ow-counter"><strong>{pins.length}</strong><em>pins</em></div>
            <div className="ow-counter ow-counter-alt"><strong>{selected.size}</strong><em>selected</em></div>
            <div className={`ow-counter ${uncategorisedCount ? 'ow-counter-warn' : ''}`}><strong>{uncategorisedCount}</strong><em>uncategorised</em></div>
          </div>
        </header>

        <div className="ow-cat-layout">
          {/* LEFT: plan */}
          <div className="ow-cat-plan">
            <div className="ow-plan-toolbar">
              <div className="ow-plan-tools">
                <button className="ow-tool" onClick={() => setScale(s => Math.max(s - 0.25, 0.3))}><FaSearchMinus /></button>
                <span className="ow-zoom-readout">{Math.round(scale * 100)}%</span>
                <button className="ow-tool" onClick={() => setScale(s => Math.min(s + 0.25, 4))}><FaSearchPlus /></button>
                <button className="ow-tool" onClick={() => { setScale(1); setOffset({ x: 0, y: 0 }); }}><FaCompress /></button>
              </div>
              {selected.size > 0 && (
                <div className="ow-assign-bar">
                  <span>Assign {selected.size} to</span>
                  {categories.map(c => (
                    <button key={c.id} className="ow-assign-chip" style={{ borderColor: c.color, color: c.color }}
                            onClick={() => assignSelectedTo(c.id)}>
                      <span className="ow-assign-dot" style={{ background: c.color }} />
                      {c.name}
                    </button>
                  ))}
                  <button className="ow-assign-chip ow-assign-clear" onClick={() => assignSelectedTo(null)}>Uncategorise</button>
                </div>
              )}
            </div>

            <div
              className={`ow-plan-viewport ${viewportActive ? 'is-zoom-active' : ''}`}
              ref={vpRef}
              onMouseDown={onMouseDownPlan}
            >
              {!imageSrc && <div className="ow-plan-loading">Rendering plan…</div>}
              {imageSrc && (
                <div className="ow-plan-stage" style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})` }}>
                  <div className="ow-plan-frame" style={{
                    ...(frameSize ? { width: frameSize.w, height: frameSize.h } : {}),
                    '--pin-counter-scale': Math.max(0.25, 1 / Math.pow(scale, 1.25)),
                  }}>
                    <img ref={imgRef} src={imageSrc} alt="Floor plan" className="ow-plan-image" draggable={false} onLoad={recalcFrameSize} />
                    {pins.map(pin => {
                      const cat = catsById[pin.category_id];
                      const color = cat?.color || '#EF4444';
                      const isSel = selected.has(pin.id);
                      return (
                        <button key={pin.id}
                                className={`ow-pin ${isSel ? 'is-selected' : ''} ${!cat ? 'is-uncat' : ''}`}
                                style={{ left: `${pin.x_percent}%`, top: `${pin.y_percent}%` }}
                                onMouseDown={e => onMouseDownPin(e, pin)}>
                          <span className="ow-pin-body" style={{ background: color, '--pin-color': color }}>{pin.temp_number}</span>
                        </button>
                      );
                    })}
                    {marqueeRect && (
                      <div className="ow-marquee" style={{
                        left: `${marqueeRect.left}%`, top: `${marqueeRect.top}%`,
                        width: `${marqueeRect.width}%`, height: `${marqueeRect.height}%`
                      }} />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: sidebar */}
          <aside className="ow-cat-side">
            {/* Filter tabs */}
            <div className="ow-cat-filter-tabs">
              <button
                className={`ow-cat-filter-tab ${sidebarFilter === 'uncategorised' ? 'is-active' : ''}`}
                onClick={() => setSidebarFilter('uncategorised')}
              >
                Uncategorised
                <span className={`ow-cat-filter-badge ${uncategorisedCount > 0 ? 'is-red' : 'is-muted'}`}>
                  {uncategorisedCount}
                </span>
              </button>
              <button
                className={`ow-cat-filter-tab ${sidebarFilter === 'categorised' ? 'is-active' : ''}`}
                onClick={() => setSidebarFilter('categorised')}
              >
                Categorised
                <span className="ow-cat-filter-badge is-green">
                  {pins.length - uncategorisedCount}
                </span>
              </button>
            </div>

            {/* ── Uncategorised view ── */}
            {sidebarFilter === 'uncategorised' && (
              <>
                {uncategorisedCount === 0 ? (
                  <div className="ow-cat-empty">All pins have been categorised.</div>
                ) : (
                  <>
                    {/* Toolbar: select-all + bulk actions */}
                    <div className="ow-uncat-toolbar">
                      {(() => {
                        const allSelected = uncategorisedCount > 0 && uncatPins.every(p => selected.has(p.id));
                        return (
                          <button
                            className="ow-btn ow-btn-ghost ow-btn-sm"
                            onClick={() => {
                              if (allSelected) {
                                setSelected(prev => {
                                  const n = new Set(prev);
                                  uncatPins.forEach(p => n.delete(p.id));
                                  return n;
                                });
                              } else {
                                setSelected(prev => {
                                  const n = new Set(prev);
                                  uncatPins.forEach(p => n.add(p.id));
                                  return n;
                                });
                              }
                            }}
                          >
                            {allSelected ? `Unselect all ${uncategorisedCount}` : `Select all ${uncategorisedCount}`}
                          </button>
                        );
                      })()}
                      {uncatSelected.length > 0 && (
                        <button
                          className={`ow-btn ow-btn-ghost ow-btn-sm ${bulkPanelOpen ? 'is-active' : ''}`}
                          onClick={() => setBulkPanelOpen(v => !v)}
                        >
                          <FaLayerGroup /> Bulk actions ({uncatSelected.length})
                          <FaChevronDown className={`ow-bulk-chevron ${bulkPanelOpen ? 'open' : ''}`} />
                        </button>
                      )}
                    </div>

                    {/* Bulk actions panel */}
                    {bulkPanelOpen && (
                      <div className="ow-bulk-panel">
                        <div className="ow-bulk-panel-head">
                          <FaTag /> Set classification for {uncatSelected.length} selected pin{uncatSelected.length !== 1 ? 's' : ''}
                        </div>
                        <BulkFormFields
                          form={bulkForm}
                          moduleKey={session.module_key}
                          assetTypeOptions={assetTypeOptions}
                          onUpdate={updateBulkForm}
                        />
                        <div className="ow-bulk-panel-foot">
                          <p className="ow-bulk-hint">Sequence numbers are entered individually per pin after applying.</p>
                          <button className="ow-btn ow-btn-primary ow-btn-sm" onClick={handleBulkApply}>
                            <FaCheck /> Apply to {uncatSelected.length} pin{uncatSelected.length !== 1 ? 's' : ''}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Uncategorised pin list with accordion */}
                    <ul className="ow-uncat-pin-list">
                      {pins.filter(p => !p.category_id).map(pin => {
                        const isExpanded = expandedPinId === pin.id;
                        const isSel = selected.has(pin.id);
                        const form = pinForms[pin.id];
                        const sectionDef = SECTION_DEFS[session.module_key];
                        const prefilledLabel = form?.assetTypeKey
                          ? (sectionDef?.assetTypes[form.assetTypeKey]?.label || form.assetTypeKey)
                          : null;

                        return (
                          <li
                            key={pin.id}
                            className={`ow-uncat-pin-item ${isSel ? 'is-selected' : ''} ${isExpanded ? 'is-expanded' : ''}`}
                          >
                            <div className="ow-uncat-pin-header" onClick={() => handlePinItemClick(pin)}>
                              <button
                                className={`ow-uncat-pin-check ${isSel ? 'is-checked' : ''}`}
                                onClick={e => togglePinSelected(e, pin.id)}
                                title={isSel ? 'Deselect' : 'Select'}
                                type="button"
                              >
                                {isSel && <FaCheck />}
                              </button>
                              <span className="ow-uncat-pin-dot" />
                              <span className="ow-uncat-pin-label">Pin {pin.temp_number}</span>
                              {prefilledLabel && !isExpanded && (
                                <span className="ow-uncat-pin-prefilled">{prefilledLabel}</span>
                              )}
                              <FaChevronDown className={`ow-uncat-pin-chevron ${isExpanded ? 'open' : ''}`} />
                            </div>

                            {isExpanded && (
                              <ClassifyForm
                                pin={pin}
                                form={form || makeDefaultForm(session.module_key, assetTypeOptions)}
                                moduleKey={session.module_key}
                                assetTypeOptions={assetTypeOptions}
                                onUpdate={(field, value) => updatePinForm(pin.id, field, value)}
                                onCategorise={() => handleCategorisePin(pin)}
                                saving={categorisingPinId === pin.id}
                              />
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </>
            )}

            {/* ── Categorised view ── */}
            {sidebarFilter === 'categorised' && (
              <>
                <div className="ow-cat-side-add">
                  <button className="ow-btn ow-btn-ghost ow-btn-sm" onClick={startAddCategory} disabled={!!newCategoryDraft}>
                    <FaPlus /> Add Category
                  </button>
                </div>

                {categories.length === 0 && !newCategoryDraft && (
                  <div className="ow-cat-empty">
                    No categories yet. Categorise pins from the Uncategorised tab.
                  </div>
                )}

                <ul className="ow-cat-list">
                  {categories.map(cat => {
                    const catPins = pins.filter(p => p.category_id === cat.id);
                    const sectionLabel = SECTION_DEFS[session.module_key]?.assetTypes[cat.target_asset_type_key]?.label
                      || assetTypeOptions.find(a => a.key === cat.target_asset_type_key)?.label
                      || cat.target_asset_type_key;
                    return (
                      <li key={cat.id} className="ow-cat-group">
                        {/* Category header */}
                        {editingCatId === cat.id ? (
                          <div className={`ow-cat-item is-editing`}>
                            <EditCategoryRow cat={cat} assetTypeOptions={assetTypeOptions}
                                             onSave={patch => saveCategoryEdit(cat, patch)}
                                             onCancel={() => setEditingCatId(null)} />
                          </div>
                        ) : (
                          <div className="ow-cat-item">
                            <span className="ow-cat-swatch" style={{ background: cat.color }} />
                            <div className="ow-cat-copy">
                              <strong>{cat.name}</strong>
                              <em>{sectionLabel}</em>
                            </div>
                            <span className="ow-cat-count">{catPins.length}</span>
                            <div className="ow-cat-actions">
                              <button className="ow-icon-btn" onClick={() => setEditingCatId(cat.id)}><FaPencilAlt /></button>
                              <button className="ow-icon-btn" onClick={() => removeCategory(cat)}><FaTrash /></button>
                            </div>
                          </div>
                        )}

                        {/* Pins in this category */}
                        {catPins.length > 0 && (
                          <ul className="ow-cat-pin-sublist">
                            {catPins.map(pin => {
                              const isExpanded = expandedPinId === pin.id;
                              let meta = {};
                              try { meta = JSON.parse(pin.name_reference || '{}'); } catch {}
                              const subLabel = (() => {
                                const sd = SECTION_DEFS[session.module_key];
                                const tk = pinForms[pin.id]?.assetTypeKey || '';
                                return sd?.assetTypes[tk]?.subtypes?.find(s => s.value === (pinForms[pin.id]?.subtypeValue || meta.subtype))?.label || '';
                              })();

                              return (
                                <li
                                  key={pin.id}
                                  className={`ow-uncat-pin-item ow-cat-pin-item ${isExpanded ? 'is-expanded' : ''}`}
                                >
                                  <div className="ow-uncat-pin-header" onClick={() => handlePinItemClick(pin)}>
                                    <span className="ow-uncat-pin-dot" style={{ background: cat.color }} />
                                    <span className="ow-uncat-pin-label">Pin {pin.temp_number}</span>
                                    {meta.seq && !isExpanded && (
                                      <span className="ow-uncat-pin-prefilled ow-cat-seq">#{meta.seq}</span>
                                    )}
                                    {subLabel && !isExpanded && (
                                      <span className="ow-uncat-pin-prefilled">{subLabel}</span>
                                    )}
                                    <FaChevronDown className={`ow-uncat-pin-chevron ${isExpanded ? 'open' : ''}`} />
                                  </div>
                                  {isExpanded && (
                                    <ClassifyForm
                                      pin={pin}
                                      form={pinForms[pin.id] || makeFormFromMeta(meta, session.module_key, assetTypeOptions)}
                                      moduleKey={session.module_key}
                                      assetTypeOptions={assetTypeOptions}
                                      onUpdate={(field, value) => updatePinForm(pin.id, field, value)}
                                      onCategorise={() => handleCategorisePin(pin)}
                                      saving={categorisingPinId === pin.id}
                                    />
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })}

                  {newCategoryDraft && (
                    <li className="ow-cat-item is-editing">
                      <EditCategoryRow cat={newCategoryDraft} isNew assetTypeOptions={assetTypeOptions}
                                       onChange={patch => setNewCategoryDraft(prev => ({ ...prev, ...patch }))}
                                       onSave={saveNewCategory} onCancel={cancelNewCategory} />
                    </li>
                  )}
                </ul>
              </>
            )}
          </aside>
        </div>

        <footer className="ow-card-foot">
          <span className="ow-foot-note">
            {uncategorisedCount > 0
              ? `${uncategorisedCount} pin${uncategorisedCount > 1 ? 's' : ''} still need a category.`
              : 'Every pin has a category.'}
          </span>
          <button className="ow-btn ow-btn-primary" onClick={continueNext} disabled={uncategorisedCount > 0 || !pins.length}>
            Continue <FaArrowRight />
          </button>
        </footer>
      </section>
    </div>
  );
}

function EditCategoryRow({ cat, isNew, assetTypeOptions, onChange, onSave, onCancel }) {
  const [local, setLocal] = useState(cat);
  useEffect(() => { setLocal(cat); }, [cat.id]);
  const patch = (p) => { const next = { ...local, ...p }; setLocal(next); if (onChange) onChange(p); };

  return (
    <>
      <div className="ow-cat-colour-picker">
        {PALETTE.map(c => (
          <button key={c} className={`ow-cat-swatch-btn ${local.color === c ? 'is-active' : ''}`}
                  style={{ background: c }}
                  onClick={() => patch({ color: c })} type="button" />
        ))}
      </div>
      <div className="ow-cat-edit-fields">
        <input className="ow-input" placeholder="Category name, e.g. Double socket"
               value={local.name || ''}
               onChange={e => patch({ name: e.target.value })} autoFocus />
        <select className="ow-input" value={local.target_asset_type_key || ''}
                onChange={e => patch({ target_asset_type_key: e.target.value })}>
          <option value="">Select asset type…</option>
          {assetTypeOptions.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
        </select>
      </div>
      <div className="ow-cat-edit-actions">
        <button className="ow-icon-btn ow-icon-btn-primary" onClick={() => onSave(isNew ? undefined : local)}><FaCheck /></button>
        <button className="ow-icon-btn" onClick={onCancel}><FaTimes /></button>
      </div>
    </>
  );
}

function nextColor(categories, palette) {
  const used = new Set(categories.map(c => c.color));
  for (const c of palette) if (!used.has(c)) return c;
  return palette[categories.length % palette.length];
}

export default Step3Categorise;
